import {
    CoreFetch,
    DocumentApi,
    GetProviderMessageRequest,
    ListProviderCommentsOrderEnum,
    ListProviderMessagesRequest,
    MessageApi,
    MessageCategory,
    MessageLinkEntityType,
    ProviderComment,
    ProviderCommentSortType,
    ProviderCreateDocumentType,
    ProviderDocument,
    ProviderDocumentUrl,
    ProviderMessage,
    ProviderUser,
    UserApi,
} from '@enter/core';
import _ from 'lodash';
import { ICommentCompose, IMessageCompose, IMessageDetail, IMessageListing } from './message.store';
import { equalsFilter, fetch, parseResponse } from './util';

export class MessageService {
    public async fetchMessageListing(request: ListProviderMessagesRequest): Promise<IMessageListing | undefined> {
        const res = await fetch(request, req => CoreFetch<MessageApi>(MessageApi).listProviderMessagesRaw(req));
        if (res) {
            const messages = await parseResponse(res);
            const total = Number(await res.raw.headers.get('pagination-total')) || _.size(messages);
            return {
                messages,
                total,
            };
        }
    }
    public async fetchMessageCount(request: ListProviderMessagesRequest): Promise<number> {
        let total = 0;
        const res = await fetch(request, req => CoreFetch<MessageApi>(MessageApi).listProviderMessagesRaw(req));
        if (res) {
            total = Number(await res.raw.headers.get('pagination-total'));
        }
        return total;
    }
    public async fetchMessages(request: ListProviderMessagesRequest): Promise<ProviderMessage[] | undefined> {
        return fetch(request, req => CoreFetch<MessageApi>(MessageApi).listProviderMessages(req));
    }
    public async fetchMessage(request: GetProviderMessageRequest): Promise<ProviderMessage | undefined> {
        const message = await fetch(request, req => CoreFetch<MessageApi>(MessageApi).getProviderMessage(req));
        return message;
    }
    public async fetchMessageDetail(message: ProviderMessage): Promise<IMessageDetail | undefined> {
        if (!message || !message.id) {
            return undefined;
        }
        const messageId = message.id as string;

        // let comments = await this.fetchCommentsByMessageId(messageId);
        const comments = _.orderBy(message.comments, 'created', 'asc'); // NB: order comments from oldest-to-newest

        const documentIds = _.flatMap(comments, 'documents');
        const documents = !_.isEmpty(documentIds) ? await this.fetchDocumentsById(documentIds) : [];
        return {
            message,
            comments,
            documents,
        };
    }
    public async fetchDocumentUrl(documentId: string): Promise<ProviderDocumentUrl | undefined> {
        const documentUrl = await this.fetchDocumentUrlById(documentId);
        return documentUrl;
    }

    public async composeMessage(messageCompose: IMessageCompose): Promise<IMessageDetail> {
        // create message with subject, priority, and dueDate (claimId is deprecated - use /link endpoint instead)
        const providerCreateMessagePayload = {
            subject: messageCompose.subject,
            priority: messageCompose.priority as any,
            dueDate: messageCompose.dueDate,
        };
        const message = await CoreFetch<MessageApi>(MessageApi).createProviderMessage({
            providerCreateMessagePayload,
        });
        
        // Link claims using /link endpoint if provided
        if (messageCompose.claimIds && messageCompose.claimIds.length > 0 && message.id) {
            await CoreFetch<MessageApi>(MessageApi).postProviderMessageLink({
                id: message.id,
                providerMessageLinkPayload: {
                    entityType: MessageLinkEntityType.CLAIM,
                    entityIds: messageCompose.claimIds,
                },
            });
        }
        
        // Link categories using /link endpoint if provided
        if (messageCompose.categoryIds && messageCompose.categoryIds.length > 0 && message.id) {
            await CoreFetch<MessageApi>(MessageApi).postProviderMessageLink({
                id: message.id,
                providerMessageLinkPayload: {
                    entityType: MessageLinkEntityType.MESSAGECATEGORY,
                    entityIds: messageCompose.categoryIds,
                },
            });
        }
        
        // Link assigned users using /link endpoint if provided
        if (messageCompose.assignedUserIds && messageCompose.assignedUserIds.length > 0 && message.id) {
            await CoreFetch<MessageApi>(MessageApi).postProviderMessageLink({
                id: message.id,
                providerMessageLinkPayload: {
                    entityType: MessageLinkEntityType.BANKUSER,
                    entityIds: messageCompose.assignedUserIds,
                },
            });
        }
        
        // create documents
        const { documents } = await this.createDocuments(messageCompose.files);
        // create comment
        const comment = await CoreFetch<MessageApi>(MessageApi).createProviderComment({
            id: message.id as string,
            providerCreateCommentPayload: {
                body: messageCompose.body,
                documents: _.compact(_.map(documents, 'id')),
            },
        });
        return {
            message: _.assign(message, {
                documents: _.size(documents),
                comments: 1,
            }),
            comments: [comment],
            documents,
        };
    }

    public async composeComment(commentCompose: ICommentCompose): Promise<ProviderComment> {
        // create documents
        const { documents } = await this.createDocuments(commentCompose.files);
        // create comment
        const comment = await CoreFetch<MessageApi>(MessageApi).createProviderComment({
            id: commentCompose.messageId,
            providerCreateCommentPayload: {
                body: commentCompose.body || ' ',
                documents: _.compact(_.map(documents, 'id')),
            },
        });
        return comment;
    }

    public async markAsOpen(id: string): Promise<void> {
        await CoreFetch<MessageApi>(MessageApi).setProviderMessageOpen({ id });
    }

    public async markAsClosed(id: string): Promise<void> {
        await CoreFetch<MessageApi>(MessageApi).setProviderMessageClosed({ id });
    }

    public async markAsRead(id: string): Promise<void> {
        await CoreFetch<MessageApi>(MessageApi).setProviderMessageRead({ id });
    }

    public async markAsUnread(id: string): Promise<void> {
        await CoreFetch<MessageApi>(MessageApi).setProviderMessageUnread({ id });
    }

    public async fetchMessageCategories(): Promise<MessageCategory[]> {
        return CoreFetch<MessageApi>(MessageApi).listProviderMessageCategory();
    }

    public async fetchProviderUsers(): Promise<ProviderUser[]> {
        return CoreFetch<UserApi>(UserApi).listProviderUsers({
            filterActive: 'EQ:1',
        });
    }

    public embed = async (id: string): Promise<ProviderDocumentUrl> => {
        return CoreFetch<DocumentApi>(DocumentApi).getProviderDocumentUrl({
            id,
        });
    };

    public download = async (id: string): Promise<ProviderDocumentUrl> => {
        return CoreFetch<DocumentApi>(DocumentApi).getProviderDocumentUrl({
            id,
        });
    };

    public fetchDocument(id: string): Promise<ProviderDocument> {
        return CoreFetch<DocumentApi>(DocumentApi).getProviderDocument({ id });
    }

    private async createDocuments(
        files: File[] | undefined,
    ): Promise<{ documents: ProviderDocument[]; documentUrls: ProviderDocumentUrl[] }> {
        const promises = _.map(
            files,
            async (file: File): Promise<[ProviderDocument, ProviderDocumentUrl]> => {
                // create document
                const document = await CoreFetch<DocumentApi>(DocumentApi).createProviderDocument({
                    providerCreateDocumentPayload: {
                        filename: file.name,
                        documentType: ProviderCreateDocumentType.MESSAGEDOCUMENT,
                    },
                });
                if (!document || !document.id) {
                    throw new Error(`Failed to create document for ${file.name}`);
                }
                // find upload destination for document
                const documentUpload = await CoreFetch<DocumentApi>(DocumentApi).createProviderDocumentUpload({
                    id: document.id,
                    body: {},
                });
                if (!documentUpload || !documentUpload.url) {
                    throw new Error(`Failed to create document upload for ${file.name}`);
                }
                this.putFile(documentUpload.url, file);
                // create document URL
                const documentUrl = await CoreFetch<DocumentApi>(DocumentApi).getProviderDocumentUrl({
                    id: document.id,
                });
                // refresh document metadata
                await CoreFetch<DocumentApi>(DocumentApi).refreshProviderDocumentMetadata({
                    id: document.id,
                    body: {},
                });
                return [document, documentUrl];
            },
        );
        const results = await Promise.all(promises);
        const documents = _.map(results, '0');
        const documentUrls = _.map(results, '1');
        return {
            documents,
            documentUrls,
        };
    }

    private putFile = (url: string, file: File): Promise<void> => {
        return new Promise((res): void => {
            const xhr = new XMLHttpRequest();
            xhr.upload.addEventListener('load', () => res());
            xhr.open('PUT', url);
            xhr.setRequestHeader('x-amz-server-side-encryption', 'aws:kms');
            xhr.overrideMimeType(file.type);
            xhr.send(file);
        });
    };

    private fetchCommentsByMessageId(messageId: string): Promise<ProviderComment[] | undefined> {
        return fetch(messageId, (id: string) =>
            CoreFetch<MessageApi>(MessageApi).listProviderComments({
                id,
                sort: [ProviderCommentSortType.CREATED],
                order: [ListProviderCommentsOrderEnum.DESCENDING],
            }),
        );
    }

    private fetchDocumentsById(_ids: string[]): Promise<ProviderDocument[] | undefined> {
        return fetch(_ids, ids => {
            const filterId = _.flatten(_.compact(_.map(ids, id => equalsFilter(id))));
            return CoreFetch<DocumentApi>(DocumentApi).listProviderDocuments({ filterId });
        });
    }
    private fetchDocumentUrlById(documentId: string): Promise<ProviderDocumentUrl | undefined> {
        return fetch(documentId, (id: string) => CoreFetch<DocumentApi>(DocumentApi).getProviderDocumentUrl({ id }));
    }
}
